import React, { useState, useCallback } from "react";
import Header from "../Components/Header";
import { useAuth } from "../../../features/auth/AuthContext";
import DataTable from "../Components/DataTable";
import { format } from "date-fns";
import Pagination from "../Components/Pagination";
import Loader from "../Components/Loader";
import Filter from "../Components/Filter";
import FloatingAIButton from "../../FloatingAIButton/FloatingAIButton";
import { useRepoQuery } from "../hooks/useRepoQuery";
import {
  setDrawerState,
  setIsWebAllowed,
  setQueryMetaData,
} from "../../../state/reducers/chatDrawerReducers";
import { useDispatch, useSelector } from "react-redux";
import ActivityLogCards from "../Components/ActivityLogCards";

const ActivityLog = () => {
  const { token } = useAuth();
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();
  const chatDrawerState = useSelector((state) => state?.ChatDrawerState);

  const {
    data: logData = { total: null, logs: [] },
    isFetching: loading,
  } = useRepoQuery({
    token,
    endpoint: "activity-logs/",
    queryKey: "activityLogs",
    page,
    search: search || null,
    filters,
  });

  const closeFilterDrawer = () => {
    setOpenFilter(false);
  };

  const handleFilterClick = () => {
    setOpenFilter(true);
  };

  const handleApplyFilter = (filterObj) => {
    setPage(1);
    setFilters(filterObj);
    closeFilterDrawer();
  };

  const resetFilter = () => {
    setPage(1);
    setFilters(null);
    closeFilterDrawer();
  };

  const columns = [
    {
      key: "date",
      title: "Date",
      sortable: true,
      type: "date",
      render: (val) => <span>{format(new Date(val), "dd/MM/yy")}</span>,
    },
    {
      key: "action_label",
      title: "Action",
      render: (val) => (
        <div className="p-[0.3rem] w-[8rem] min-w-fit text-[#0068FF] rounded-[15px] font-normal text-[0.8rem] border !border-[#0068FF] flex items-center justify-center">
          {val}
        </div>
      ),
    },
    {
      key: "details",
      title: "Details",
      align: "left",
      width: "auto",
      render: (value) => {
        const removeBoldMarkers = value.replace(/\*\*/g, "");
        return (
          <div
            title={removeBoldMarkers}
            className="text-[#555555] font-normal text-[0.9rem] max-w-full truncate"
          >
            {value?.split(/(\*\*.*?\*\*)/g).map((part, index) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <span className="font-medium text-black" key={index}>
                  "{part.slice(2, -2)}"
                </span>
              ) : (
                part
              ),
            )}
          </div>
        );
      },
    },
  ];

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    setPage(1);
    setSearch("");
    setFilters(null);
  };

  const handleSearchChange = useCallback(
    (input) => {
      if (input === search) return;
      setSearch(input);
      setPage(1);
    },
    [search],
  );
  return (
    <>
      <Header
        searchParam={search}
        setSearchParam={handleSearchChange}
        title={"Activity Logs"}
        btnLabel={null}
        fileView={true}
        clickFilter={handleFilterClick}
        handleRefresh={handleRefresh}
        justify={window.innerWidth < 650 ? "justify-between" : null}
        searchPlaceholder="Search action or file name"
      />
      <Filter
        activityActions={true}
        updatedOn={true}
        open={openFilter}
        close={closeFilterDrawer}
        appliedFilters={filters}
        applyFilter={handleApplyFilter}
        enableResults={true}
        filterAPI="activity-logs/"
        onReset={resetFilter}
      />
      <div className="md:shadow-[0_0_12px_#00000014] px-2 md:px-8 md:py-2 md:bg-white rounded-[12px] mt-4">
        {loading ? (
          <Loader />
        ) : (
          <>
            {window.innerWidth < 650 ? (
              <ActivityLogCards logs={logData.logs} />
            ) : (
              <DataTable
                columns={columns}
                dataSource={logData.logs}
                rowKey="key"
                maxHeight="max-h-[80vh]"
                parentWidth="w-100%"
                emptyHeadLabel="No Activity Found"
                emptySecLabel="Activity will appear here once any activity happens."
              />
            )}
            <div className="my-2">
              <Pagination
                pageSize={12}
                totalCount={logData.total}
                currentPage={page}
                onPageChange={onPageChange}
              />
            </div>

            <FloatingAIButton
              onClick={() => {
                dispatch(setDrawerState(!chatDrawerState.isOpen));
                dispatch(setIsWebAllowed(true));
                dispatch(setQueryMetaData(null));
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default ActivityLog;
